/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { ChartBarIcon, ClockIcon, UsersIcon } from 'lucide-react'
import React, { CSSProperties, forwardRef } from 'react'

import styles from '@/components/quality-explorer/ActivityCard.module.css'
import { ActivityCardInfoItem } from '@/components/quality-explorer/ActivityCardInfoItem'
import { Activity } from '@/components/quality-explorer/types'
import { cn } from '@/utils/css'

/** The colour configuration for the activity card. */
const COLOUR_CONFIG = {
  Red: {
    text: 'text-kits-team',
    border: 'border-kits-team',
    hex: '#d51818',
  },
  Green: {
    text: 'text-kits-insights',
    border: 'border-kits-insights',
    hex: '#1b9147',
  },
  Blue: {
    text: 'text-kits-mini',
    border: 'border-kits-mini',
    hex: '#2f93cd',
  },
} as const

/** A description of the available properties for this component. */
type ActivityCarouselCardProps = {
  /** The activity to display on the card. */
  activity: Activity

  /** The offset of the card from the center card. */
  offset: number

  /** The mode of the carousel (showing 1 cards, or 2). */
  mode: 1 | 2

  /** A function to call when the card is clicked. */
  onClick?: () => void

  /** Additional classes to apply to the card. */
  className?: string
}

/**
 * A card that displays information about an activity. The card is styled to appear in a 3D carousel, with the center
 * card being the largest and most prominent, and the other cards being smaller and semi-transparent.
 */
const ActivityCarouselCard = forwardRef<HTMLDivElement, ActivityCarouselCardProps>(
  ({ activity, offset, mode, onClick, className }, ref) => {
    /**
     * Generate the `style` attributes for the card based on the current offset and mode. This gives the card a 3D
     * effect when it is offset from the center card by manipulating the `transform` property. The key is the
     * `translateX` value, which moves the card horizontally and `scale` which shrinks the card as it moves away from
     * the center. The `opacity` is also adjusted to make the card semi-transparent when it is not the center card.
     * The numbers used in the calculations are very specific to the design and should not be changed without testing.
     */
    const getCardStyle = (): CSSProperties => {
      const isActive = offset === 0
      const isTwoCardMode = mode === 2

      // Base values for active card
      let translateX = isActive ? (isTwoCardMode ? 30 : 22.5) : 0
      let translateZ = 0
      let rotateY = 0
      let scaleValue = 1

      if (isTwoCardMode && offset === 1) {
        translateX = 132.5
      } else if (offset > (isTwoCardMode ? 1 : 0)) {
        translateX = isTwoCardMode ? 132.5 + 15 + (offset - 1) * 11.5 : 35 + offset * 7.5
        translateZ = -100 - (isTwoCardMode ? offset - 1 : offset) * 50
        rotateY = 30
        scaleValue = Math.max(isTwoCardMode ? 0.85 - (offset - 1) * 0.1 : 0.9 - offset * 0.1, 0.5)
      } else if (!isActive) {
        translateX = offset * (isTwoCardMode ? 11.5 : 7.5) + (isTwoCardMode ? 15 : 10)
        translateZ = -100 + offset * 50
        rotateY = -30
        scaleValue = Math.max((isTwoCardMode ? 0.85 : 0.9) + offset * 0.1, 0.5)
      }

      // Opacity is based on the card's distance from the center (and varies slightly in two-card mode as two are visible)
      const opacity = Math.max(
        offset > 1 || offset <= -1 ? 1 - Math.abs(isTwoCardMode && offset > 1 ? offset - 1 : offset) * 0.25 : 1,
        0,
      )

      return {
        transform:
          opacity === 0
            ? `translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scaleValue})`
            : `translateX(${translateX}%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scaleValue})`,
        zIndex: 100 - Math.abs(offset * 10),
        opacity,
        pointerEvents: opacity === 0 ? 'none' : 'auto',
      }
    }

    /** Process the descriptions to add the new CSS classes to any `ul` tag. */
    const processDescriptions = (text?: string): string => {
      return (
        text
          ?.replace(/<ul(.*?)class="(.*?)"(.*?)>/g, `<ul$1class="$2 ${styles.list}"$3>`)
          .replace(/<ul(?![^>]*\bclass=)/g, `<ul class="${styles.list}"`) ?? ''
      )
    }
    const colourConfig = COLOUR_CONFIG[activity.kolor]

    return (
      <div
        className={cn('absolute transition-all duration-500', className)}
        style={getCardStyle()}
        onClick={onClick}
        ref={ref}
        data-offset={offset}
      >
        <div className={'bg-white rounded-2xl p-4 md:p-6 shadow-mta h-full'}>
          <h3 className={cn('text-xl font-semibold mb-4', colourConfig.text)}>
            {activity.name}: {activity.kit_name}
          </h3>

          <ul className={cn('border-t-2 border-b-2 py-3 mb-4 space-y-2', colourConfig.border)}>
            <ActivityCardInfoItem
              icon={<ClockIcon className={'w-5 h-5 text-gray-500'} />}
              label={'Time'}
              value={activity.time}
            />
            <ActivityCardInfoItem
              icon={<UsersIcon className={'w-5 h-5 text-gray-500'} />}
              label={'Participants'}
              value={activity.participants}
            />
            <ActivityCardInfoItem
              icon={<ChartBarIcon className={'w-5 h-5 text-gray-500'} />}
              label={'Level'}
              value={activity.level}
            />
          </ul>
          <div
            style={{ '--list-color': colourConfig.hex } as React.CSSProperties}
            className={'text-gray-800'}
            dangerouslySetInnerHTML={{
              __html: processDescriptions(activity.descriptions),
            }}
          />
        </div>
      </div>
    )
  },
)
ActivityCarouselCard.displayName = 'ActivityCarouselCard'

export { ActivityCarouselCard }
